import { useState } from 'react';
import { post, session, ApiError } from '../../lib/api';

/**
 * Excel import / export.
 *
 * Always dry-runs first: you see exactly what would be created, updated and
 * rejected — with per-row, per-column error messages — before anything is
 * written. Re-importing the same file is safe (upsert keyed on SKU), which makes
 * "export → edit in Excel → re-import" a legitimate bulk-edit workflow.
 */
export default function ImportExport() {
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [applied, setApplied] = useState(false);

  async function download(path: string, filename: string) {
    const res = await fetch(path, { headers: { Authorization: `Bearer ${session.token()}` } });
    if (!res.ok) { setError(`Download failed (HTTP ${res.status})`); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function upload(dryRun: boolean) {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const b64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve((r.result as string).split(',')[1]);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const res = await post<any>('/api/v1/admin/products/import', {
        filename: file.name, fileBase64: b64, dryRun,
      });
      setReport(res);
      setApplied(!dryRun);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Import failed');
    } finally { setBusy(false); }
  }

  return (
    <div className="p-6 space-y-5 max-w-4xl">
      <h1 className="text-xl font-semibold">Import &amp; export products</h1>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-5">
          <div className="font-medium mb-1">1. Get a spreadsheet</div>
          <p className="text-sm text-gray-400 mb-4">
            Start from a blank template, or export the current catalogue, edit it in Excel and
            re-import. Re-importing is safe — rows are matched on SKU.
          </p>
          <div className="flex flex-col gap-2">
            <button className="btn-ghost"
              onClick={() => void download('/api/v1/admin/products/template', 'femi9-product-template.xlsx')}>
              Download blank template
            </button>
            <button className="btn-ghost"
              onClick={() => void download('/api/v1/admin/products/export', 'femi9-catalogue.xlsx')}>
              Export full catalogue
            </button>
          </div>
        </div>

        <div className="card p-5">
          <div className="font-medium mb-1">2. Upload</div>
          <p className="text-sm text-gray-400 mb-4">
            Nothing is written until you review the dry run and confirm.
          </p>
          <input type="file" accept=".xlsx,.xls,.csv"
            className="block w-full text-sm text-gray-300 mb-3
                       file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0
                       file:bg-gray-800 file:text-gray-200 hover:file:bg-gray-700"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setReport(null); setApplied(false); }} />
          <button className="btn-primary w-full" disabled={!file || busy} onClick={() => void upload(true)}>
            {busy ? 'Checking…' : 'Dry run — check the file'}
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-red-300 bg-red-950/50 border border-red-900 rounded-lg px-4 py-3">{error}</div>}

      {report && (
        <div className="card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-medium">{applied ? 'Import applied' : 'Dry run result'}</div>
            <span className="text-xs text-gray-500">{report.filename}</span>
          </div>

          <div className="grid grid-cols-4 gap-3 text-center">
            <Box label="Rows" value={report.totalRows} />
            <Box label="Create" value={report.willCreate} tone="emerald" />
            <Box label="Update" value={report.willUpdate} tone="sky" />
            <Box label="Rejected" value={report.rejected} tone={report.rejected ? 'red' : undefined} />
          </div>

          {report.errors?.length > 0 && (
            <div>
              <div className="label text-red-300">Rejected rows</div>
              <div className="max-h-56 overflow-y-auto card divide-y divide-gray-800 text-xs">
                {report.errors.map((e: any, i: number) => (
                  <div key={i} className="px-3 py-1.5 flex gap-3">
                    <span className="text-gray-500 shrink-0">row {e.row}</span>
                    {e.column && <span className="font-mono text-amber-400 shrink-0">{e.column}</span>}
                    <span className="text-gray-300">{e.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.previewUpdate?.length > 0 && (
            <div>
              <div className="label">Price changes preview</div>
              <div className="card divide-y divide-gray-800 text-xs">
                {report.previewUpdate.map((r: any, i: number) => (
                  <div key={i} className="px-3 py-1.5 flex justify-between">
                    <span className="font-mono">{r.sku}</span>
                    <span className="money">
                      <span className="text-gray-500">{r.oldPrice}</span>
                      <span className="mx-2">→</span>
                      <span className={r.oldPrice !== r.newPrice ? 'text-amber-300' : ''}>{r.newPrice}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.previewCreate?.length > 0 && (
            <div>
              <div className="label">New products preview</div>
              <div className="card divide-y divide-gray-800 text-xs">
                {report.previewCreate.map((r: any, i: number) => (
                  <div key={i} className="px-3 py-1.5 flex justify-between">
                    <span><span className="font-mono">{r.sku}</span> <span className="text-gray-400">{r.title}</span></span>
                    <span className="money">{r.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!applied && (
            <button className="btn-primary w-full h-11"
              disabled={busy || (report.willCreate + report.willUpdate) === 0}
              onClick={() => void upload(false)}>
              {busy ? 'Applying…' : `Apply — create ${report.willCreate}, update ${report.willUpdate}`}
            </button>
          )}
          {applied && (
            <div className="text-sm text-emerald-300 bg-emerald-950/40 border border-emerald-900 rounded-lg px-4 py-2">
              {report.message} · job {report.jobId}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Box({ label, value, tone }: { label: string; value: number; tone?: 'emerald' | 'sky' | 'red' }) {
  const c = tone === 'emerald' ? 'text-emerald-300' : tone === 'sky' ? 'text-sky-300' : tone === 'red' ? 'text-red-300' : '';
  return (
    <div className="card p-3">
      <div className={`money text-2xl font-bold ${c}`}>{value}</div>
      <div className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}
